import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createRoom } from '../services/videocall/roomService'
import { useRoom } from '../context/RoomContext'
import Button from '../components/ui/Button'
import { isValidRoomId } from '../utils/videocall/validators'

export default function HomePage() {
  const [roomIdInput, setRoomIdInput] = useState('')
  const [error, setError] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const navigate = useNavigate()
  const { setAsCreator } = useRoom()

  const handleCreateRoom = async () => {
    setError('')
    setIsCreating(true)
    
    try {
      const data = await createRoom()
      // Mark user as creator/interviewer
      setAsCreator(data.roomId)
      // Navigate to room with creator flag
      navigate(`/video-call/${data.roomId}?creator=true`)
    } catch (error) {
      setError('Failed to create room. Please try again.')
      console.error('Create room error:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleJoinRoom = () => {
    setError('')

    if (!roomIdInput.trim()) {
      setError('Please enter a room ID')
      return
    }

    if (!isValidRoomId(roomIdInput.trim())) {
      setError('Invalid room ID format')
      return
    }

    // Navigate to room as interviewee
    navigate(`/video-call/${roomIdInput.trim()}`)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleJoinRoom()
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl animate-fade-in-up">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-3 flex items-center justify-center gap-3">
            <span className="text-4xl">🎥</span>
            Video Interview
          </h1>
          <p className="text-gray-600 text-sm">
            AI-powered interview recording with automatic transcription
          </p>
        </div>

        {/* Main Cards Container */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Create Room Card */}
          <div className="bg-white shadow-lg border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-2 text-gray-800">Start New Interview</h2>
            <p className="text-gray-600 mb-4 text-sm">
              Create a new interview room as the interviewer
            </p>
            <Button
              variant="default"
              className="w-full py-2.5"
              disabled={isCreating}
              onClick={handleCreateRoom}
            >
              {isCreating ? 'Creating Room...' : 'Create Interview Room'}
            </Button>
          </div>

          {/* Join Room Card */}
          <div className="bg-white shadow-lg border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-2 text-gray-800">Join Interview</h2>
            <p className="text-gray-600 mb-4 text-sm">
              Enter the room ID provided by the interviewer
            </p>
            <div className="flex flex-col gap-3">
              <input
                type="text"
                className="w-full px-3 py-2.5 border border-gray-300 rounded
                  text-gray-900 text-sm transition-all duration-300
                  focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500
                  placeholder:text-gray-500"
                placeholder="Enter Room ID"
                value={roomIdInput}
                onChange={(e) => setRoomIdInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <Button
                variant="secondary"
                className="w-full py-2.5"
                onClick={handleJoinRoom}
              >
                Join Room
              </Button>
            </div>
            {error && (
              <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6">
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center shadow-sm">
            <span className="text-2xl block mb-2">📹</span>
            <h3 className="text-sm font-semibold mb-1 text-gray-800">HD Video & Audio</h3>
            <p className="text-xs text-gray-600">Crystal-clear video calls</p>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center shadow-sm">
            <span className="text-2xl block mb-2">🎙️</span>
            <h3 className="text-sm font-semibold mb-1 text-gray-800">Auto Recording</h3>
            <p className="text-xs text-gray-600">Cloud recording via Twilio</p>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center shadow-sm">
            <span className="text-2xl block mb-2">📝</span>
            <h3 className="text-sm font-semibold mb-1 text-gray-800">AI Transcription</h3>
            <p className="text-xs text-gray-600">Live transcription</p>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg text-center shadow-sm">
            <span className="text-2xl block mb-2">🎯</span>
            <h3 className="text-sm font-semibold mb-1 text-gray-800">Smart Assessment</h3>
            <p className="text-xs text-gray-600">AI-powered evaluation</p>
          </div>
        </div>

        {/* Footer Links */}
        <div className="flex justify-center gap-6 mt-6">
          <button
            className="text-gray-600 hover:text-green-600 text-sm transition-colors duration-300 underline"
            onClick={() => navigate('/video-call/transcripts')}
          >
            View Transcripts
          </button>
          <button
            className="text-gray-600 hover:text-green-600 text-sm transition-colors duration-300 underline"
            onClick={() => navigate('/video-call/dashboard')}
          >
            Dashboard
          </button>
        </div>
      </div>
    </div>
  )
}
